import { ReactNode } from 'react'
import cn from 'classnames'
import { LinkButton } from '../shell/Card'

export interface IGlimpseRow {
   id: string
   icon: ReactNode
   label: string
   value: ReactNode
   sub?: ReactNode
   /** Missing or still being asked for. */
   pending?: boolean
   /** Lit while its editor is open on the right. */
   active?: boolean
   onEdit?: () => void
   editLabel?: string
}

/**
 * The left-hand column of the campaign composer: every detail of the
 * campaign at a glance, each with its own Edit. The right-hand column only
 * asks for the filters and the voice; everything else is already decided
 * from the setup and can be changed here.
 */
export const CampaignGlimpse = ({ title, rows }: { title: string; rows: IGlimpseRow[] }) => (
   <aside
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 md:p-4"
      data-testid="campaign-glimpse"
   >
      <p className="text-white font-semibold text-sm px-1 pb-3">{title}</p>
      <ul className="space-y-1.5">
         {rows.map((row) => (
            <li
               key={row.id}
               data-testid={`glimpse-${row.id}`}
               data-state={row.pending ? 'pending' : 'set'}
               className={cn(
                  'flex items-start gap-3 rounded-xl px-3 py-2.5 border transition-colors',
                  row.active
                     ? 'border-[#9F7BFF]/40 bg-[#9F7BFF]/10'
                     : 'border-transparent'
               )}
            >
               <span
                  className={cn(
                     'flex shrink-0 items-center justify-center w-7 h-7 rounded-lg',
                     row.pending ? 'bg-white/[0.04] text-white/35' : 'bg-[#9F7BFF]/15 text-[#A684FF]'
                  )}
               >
                  {row.icon}
               </span>
               <span className="flex-1 min-w-0">
                  <span className="flex items-center justify-between gap-2">
                     <span className="text-[11px] uppercase tracking-wide text-white/40">{row.label}</span>
                     {row.onEdit && (
                        <LinkButton onClick={row.onEdit} className="!text-xs" testId={`edit-${row.id}`}>
                           {row.editLabel || 'Edit'}
                        </LinkButton>
                     )}
                  </span>
                  <span
                     className={cn(
                        'block text-[13px] leading-5 mt-0.5',
                        row.pending ? 'text-white/45 italic' : 'text-white'
                     )}
                  >
                     {row.value}
                  </span>
                  {row.sub && <span className="block text-xs text-white/45 mt-0.5">{row.sub}</span>}
               </span>
            </li>
         ))}
      </ul>
   </aside>
)
